import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { CustomExpander } from '../../../../components/CustomExpander/CustomExpander';
import { FilterExpanderWithListOfCheckboxes } from '../../../../components/CustomExpander/componenets/filter-expander-with-list-of-checkboxes';
import globalSettingsBranches from '../../../../stores/global-settings-branches';
import { observer } from 'mobx-react-lite';

interface SalaryHistorySideBarProps {
  startDate: Date | null;
  endDate: Date | null;
  branchId: number | null;
  onFiltersChange: (
    startDate: Date | null,
    endDate: Date | null,
    branchId: number | null,
  ) => void;
}

export const SalaryHistorySideBar: React.FC<SalaryHistorySideBarProps> =
  observer(({ startDate, endDate, branchId, onFiltersChange }) => {
    const [localStartDate, setLocalStartDate] = useState<Date | null>(
      startDate,
    );
    const [localEndDate, setLocalEndDate] = useState<Date | null>(endDate);
    const [localBranchId, setLocalBranchId] = useState<number | null>(branchId);

    const branches = globalSettingsBranches.branches;
    const branchItems = useMemo(
      () => (branches ?? []).filter((b) => Number.isInteger(b.id) && b.id >= 1),
      [branches],
    );
    const didInitBranchSelectionRef = useRef(false);

    useEffect(() => {
      // keep local state in sync when parent changes (e.g. init)
      setLocalStartDate(startDate);
      setLocalEndDate(endDate);
    }, [startDate, endDate]);

    useEffect(() => {
      setLocalBranchId(branchId);
    }, [branchId]);

    useEffect(() => {
      if (didInitBranchSelectionRef.current) return;
      if (branchItems.length === 0) return;
      if (localBranchId == null) {
        setLocalBranchId(branchItems[0]?.id ?? null);
      }
      didInitBranchSelectionRef.current = true;
    }, [branchItems, localBranchId]);

    const handleSearch = () => {
      onFiltersChange(localStartDate, localEndDate, localBranchId);
    };

    /** Single-branch selection: choose another branch replaces; click again clears. */
    const handleBranchToggle = (id: number) => {
      setLocalBranchId((prev) => {
        if (prev === id) return null;
        return id;
      });
    };

    const styles = {
      container: {
        height: 'calc(100vh - 185px)',
        width: '250px',
        maxWidth: '250px',
        minWidth: '250px',
        maxHeight: '100vh',
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column' as const,
      },
      header: {
        fontSize: '1.2rem',
        fontWeight: 'bold',
        marginBottom: '16px',
        color: '#333',
      },
      content: {
        flex: 1,
        overflowY: 'auto' as const,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        paddingRight: '4px',
      },
      dateRangeContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        padding: '8px',
      },
      calendarWrapper: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
      },
      calendarLabel: {
        fontSize: '0.9rem',
        color: '#666',
      },
      buttonContainer: {
        marginTop: '16px',
        padding: '8px',
        borderTop: '1px solid #e9ecef',
      },
      searchButton: {
        width: '100%',
        backgroundColor: '#2196F3',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        padding: '12px',
        fontSize: '1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
      },
    };

    return (
      <div style={styles.container}>
        <div style={styles.header}>היסטוריית משכורות</div>
        <div style={styles.content}>
          <FilterExpanderWithListOfCheckboxes
            title="שלוחה"
            items={branchItems}
            selectedItems={localBranchId != null ? [localBranchId] : []}
            onItemToggle={handleBranchToggle}
            getItemLabel={(b) => b.branchName}
            getItemKey={(b) => b.id}
            defaultExpanded={true}
            searchPlaceholder="חפש שלוחה..."
            allowSelectAll={false}
            allowClear
          />
          <CustomExpander>
            <div>טווח תאריכי יצירה</div>
            <div style={styles.dateRangeContainer}>
              <div style={styles.calendarWrapper}>
                <span style={styles.calendarLabel}>תאריך התחלה:</span>
                <Calendar
                  value={localStartDate}
                  onChange={(e) => setLocalStartDate(e.value as Date)}
                  dateFormat="dd/mm/yy"
                  showIcon
                  iconPos="right"
                  style={{
                    height: '36px',
                    width: '100%',
                    direction: 'ltr',
                  }}
                  inputStyle={{ direction: 'rtl' }}
                />
              </div>
              <div style={styles.calendarWrapper}>
                <span style={styles.calendarLabel}>תאריך סיום (לא כולל):</span>
                <Calendar
                  value={localEndDate}
                  onChange={(e) => setLocalEndDate(e.value as Date)}
                  dateFormat="dd/mm/yy"
                  showIcon
                  iconPos="right"
                  style={{
                    height: '36px',
                    width: '100%',
                    direction: 'ltr',
                  }}
                  inputStyle={{ direction: 'rtl' }}
                  minDate={localStartDate || undefined}
                />
              </div>
            </div>
          </CustomExpander>
        </div>

        <div style={styles.buttonContainer}>
          <Button
            label="חפש"
            icon="pi pi-search"
            style={styles.searchButton}
            onClick={handleSearch}
          />
        </div>
      </div>
    );
  });
